import React, { useState, useEffect } from 'react';
import { FiPlus, FiChevronLeft } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import MembersList from './MembersList';
import AddMember from './AddMember';
import EditMember from './EditMember';
import ViewMember from './ViewMember';
import './Members.css';
// const url = import.meta.env.VITE_BACKEND_URL;
const url = process.env.REACT_APP_BACKEND_URL;

const Members = () => {
  const [members, setMembers] = useState([]);
  const [registerTypes, setRegisterTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list');
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    fetchData();
    
    // Check URL params to set view mode
    if (params.id) {
      if (window.location.pathname.includes('/view/')) {
        setView('view');
        fetchMemberDetails(params.id);
      } else if (window.location.pathname.includes('/edit/')) {
        setView('edit');
        fetchMemberDetails(params.id);
      }
    } else if (window.location.pathname.includes('/add')) {
      setView('add');
    }
  }, [params.id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [membersRes, regTypeRes, countryRes, regionRes, districtRes] = await Promise.all([
        axios.get(`${url}/api/members`),
        axios.get(`${url}/api/registrationtype`),
        axios.get(`${url}/api/country`),
        axios.get(`${url}/api/regions`),
        axios.get(`${url}/api/district`),
      ]);

     setMembers(membersRes.data?.data || []); // <-- use the array from the backend

      setRegisterTypes(regTypeRes.data?.data || regTypeRes.data || []);
      setCountries(countryRes.data?.data || countryRes.data || []);
      setRegions(regionRes.data?.data || regionRes.data || []);
      setDistricts(districtRes.data?.data || districtRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to fetch data');
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

const fetchMemberDetails = async (id) => {
  try {
    const response = await axios.get(`${url}/api/members`);
    // Check if response.data.data exists and is an array
    const membersArray = Array.isArray(response.data) 
      ? response.data 
      : Array.isArray(response.data?.data) 
        ? response.data.data 
        : [];

    const member = membersArray.find(m => m._id === id);

    if (member) {
      setSelectedMember(member);
    } else {
      toast.error('Member not found');
    }
  } catch (err) {
    console.error('Error fetching member details:', err);
    toast.error('Failed to fetch member details');
  }
};

  const handleAddMember = () => {
    setView('add');
    navigate('/members/add');
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setView('edit');
    navigate(`/members/edit/${member._id}`);
  };

  const handleViewMember = (member) => {
    setSelectedMember(member);
    setView('view');
    navigate(`/members/view/${member._id}`);
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedMember(null);
    navigate('/members');
  };

  const handleMemberCreated = () => {
    fetchData();
    setView('list');
    navigate('/members');
    toast.success('Member created successfully!');
  };

  const handleMemberUpdated = () => {
    fetchData();
    setView('list');
    navigate('/members');
    toast.success('Member updated successfully!');
  };

  const handleMemberDeleted = () => {
    fetchData();
    setView('list');
    navigate('/members');
    toast.success('Member deleted successfully!');
  };

const filteredMembers = Array.isArray(members) ? members.filter(member =>
  `${member.fname} ${member.mname || ''} ${member.lname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
  member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  member.mobile?.includes(searchTerm)
) : [];


  if (isLoading) return <div className="loading-container"><div className="loading-spinner"></div><p>Loading members...</p></div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="members-management-container">
      <div className="members-header">
        <div className="header-content">
          <button onClick={() => navigate(-1)} className="back-btn">
            <FiChevronLeft /> Back
          </button>
          <div className="header-title">
            <h1>Member Management</h1>
            <p>{filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''} found</p>
          </div>
        </div>
        
        {view === 'list' && (
          <div className="header-actions">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <button onClick={handleAddMember} className="primary-btn">
              <FiPlus /> Add New Member
            </button>
          </div>
        )}
      </div>

      {view === 'list' && (
        <MembersList
          members={filteredMembers}
          onView={handleViewMember}
          onEdit={handleEditMember}
          onDelete={handleMemberDeleted}
          searchTerm={searchTerm}
          currentUser={currentUser}
        />
      )}

      {view === 'add' && (
        <AddMember
          registerTypes={registerTypes}
          countries={countries}
          regions={regions}
          districts={districts}
          onCancel={handleBackToList}
          onSuccess={handleMemberCreated}
        />
      )}

      {view === 'edit' && selectedMember && (
        <EditMember
          member={selectedMember}
          registerTypes={registerTypes}
          countries={countries}
          regions={regions}
          districts={districts}
          onCancel={handleBackToList}
          onSuccess={handleMemberUpdated}
        />
      )}

      {view === 'view' && selectedMember && (
        <ViewMember
          member={selectedMember}
          onEdit={() => handleEditMember(selectedMember)}
          onDelete={handleMemberDeleted}
          onClose={handleBackToList}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default Members;